import { UPDATE_PRIORITY, type Application, type Container, type RenderGroup } from "pixi.js";

/**
 * Render on demand for a game that owns its Pixi `Application` (an imported
 * game, rather than a scene on the template's `Engine`). Pixi's default ticker
 * redraws the whole stage every frame even when nothing moves, which keeps an
 * idle game at a full CPU core or more. `renderOnDemand(app)` replaces that:
 *
 * - **Scene changes:** a frame is drawn only when Pixi's render groups list a
 *   change (a moved, added, removed or re-tinted node). Running tweens move
 *   nodes, so they draw; a pause between tweens draws nothing.
 * - **Input:** pointer, wheel and key events draw for `inputWakeMs`, so hover
 *   and press feedback that only changes shaders or filters still shows.
 * - **Video:** a playing `<video>` draws every frame (its texture changes
 *   without Pixi seeing a node change).
 * - **Idle:** once nothing has been drawn for `idleAfterMs`, the ticker drops
 *   to `idleFps`, so game logic still runs but the device rests; any change
 *   brings it back to full rate.
 * - **`requestRender()`** draws for changes Pixi can't see: animated filter
 *   uniforms, a render texture redrawn by hand, a custom shader.
 *
 * Game code keeps using `app.ticker` as before.
 */
export interface OnDemandOptions {
  /** Ticker rate once nothing has been drawn for `idleAfterMs` (default 10; 0 keeps the full rate). */
  idleFps?: number;
  idleAfterMs?: number;
  /** How long frames keep drawing after an input event (default 400 ms). */
  inputWakeMs?: number;
  /** Where input events are listened for (default `window`). */
  inputTarget?: EventTarget;
  /** Draw every frame while a `<video>` element plays (default true). */
  videos?: boolean;
  /** Clock, for tests (default `performance.now`). */
  now?: () => number;
}

export type RenderReason = "requested" | "input" | "scene" | "video";

export interface OnDemandRendering {
  /** Draw frames for the next `ms` (default: the next frame) even if Pixi sees no change. */
  requestRender(ms?: number): void;
  /** Frames drawn and ticks skipped, by reason (for checks and the debug overlay). */
  readonly stats: { frames: number; skipped: number; reasons: Record<RenderReason, number> };
  /** Puts Pixi's continuous rendering back. */
  dispose(): void;
}

/** True when a render group (or one nested in it) has changes waiting to be drawn. */
export function renderGroupChanged(group: RenderGroup | null | undefined): boolean {
  if (!group) return true;
  if (group.structureDidChange || group.childrenRenderablesToUpdate.index > 0) return true;
  for (const key in group.childrenToUpdate) {
    if ((group.childrenToUpdate[key]?.index ?? 0) > 0) return true;
  }
  return group.renderGroupChildren.some(renderGroupChanged);
}

/** True when anything under `root` (normally `app.stage`) changed since the last frame. */
export function sceneChanged(root: Container): boolean {
  return renderGroupChanged(root.renderGroup);
}

const INPUT_EVENTS = ["pointerdown", "pointermove", "pointerup", "wheel", "keydown"] as const;

export function renderOnDemand(app: Application, opts: OnDemandOptions = {}): OnDemandRendering {
  const now = opts.now ?? (() => performance.now());
  const idleFps = opts.idleFps ?? 10;
  const idleAfter = opts.idleAfterMs ?? 1500;
  const inputWake = opts.inputWakeMs ?? 400;
  const target = opts.inputTarget ?? (typeof window === "undefined" ? undefined : window);
  const videos = opts.videos !== false && typeof document !== "undefined" ? document.getElementsByTagName("video") : undefined;

  const stats = { frames: 0, skipped: 0, reasons: { requested: 0, input: 0, scene: 0, video: 0 } };
  let requestedUntil = -1;
  let requestedNext = false;
  let inputUntil = -1;
  let lastFrameAt = now();

  const fullRate = () => {
    if (app.ticker.maxFPS !== 0) app.ticker.maxFPS = 0;
  };
  const videoPlaying = () => {
    if (!videos) return false;
    for (let i = 0; i < videos.length; i++) {
      const v = videos[i];
      if (v && !v.paused && !v.ended && v.readyState >= 2) return true;
    }
    return false;
  };
  const onInput = () => {
    inputUntil = now() + inputWake;
    fullRate();
  };

  const tick = () => {
    const t = now();
    const reason: RenderReason | undefined =
      requestedNext || t <= requestedUntil ? "requested" : t <= inputUntil ? "input" : sceneChanged(app.stage) ? "scene" : videoPlaying() ? "video" : undefined;
    if (reason) {
      requestedNext = false;
      app.render();
      stats.frames++;
      stats.reasons[reason]++;
      lastFrameAt = t;
      fullRate();
      return;
    }
    stats.skipped++;
    if (idleFps > 0 && t - lastFrameAt > idleAfter && app.ticker.maxFPS !== idleFps) app.ticker.maxFPS = idleFps;
  };

  // Pixi's Application draws from its ticker at LOW priority; take its place.
  app.ticker.remove(app.render, app);
  app.ticker.add(tick, undefined, UPDATE_PRIORITY.LOW);
  for (const type of INPUT_EVENTS) target?.addEventListener(type, onInput, { passive: true, capture: true });

  return {
    requestRender(ms = 0) {
      if (ms > 0) requestedUntil = Math.max(requestedUntil, now() + ms);
      else requestedNext = true;
      fullRate();
    },
    stats,
    dispose() {
      for (const type of INPUT_EVENTS) target?.removeEventListener(type, onInput, { capture: true });
      app.ticker.remove(tick);
      app.ticker.maxFPS = 0;
      app.ticker.add(app.render, app, UPDATE_PRIORITY.LOW);
    },
  };
}
